import React, {useCallback} from 'react';
import Row from '@components/Table/rows/Row';
import {useTableContext} from '@components/Table';
import {ArtistDiscographyTableColumn} from './types';
import ArtistDiscographyTitleCell from './ArtistDiscographyTitleCell';
import type {Release} from '@deezer/next/services/api/Starmania/requests/album/types';
import CheckboxCell from '@components/Table/cells/CheckboxCell';
import TextCell from '@components/Table/cells/TextCell';
import StatusCell from '@deezer/next/features/ClaimAccess/components/ArtistDiscographyTable/cells/StatusCell';

type ArtistDiscographyTableRowProps = {
	release: Release;
	isRowSelected: boolean;
	isRowLocked: boolean;
	rowIndex: number;
	onRowChange: (id: number) => void;
};

const ArtistDiscographyTableRow = ({
	release,
	isRowSelected,
	isRowLocked,
	rowIndex,
	onRowChange,
}: ArtistDiscographyTableRowProps) => {
	const {columns} = useTableContext<ArtistDiscographyTableColumn>();

	const handleChange = useCallback(() => {
		if (isRowLocked) return;
		onRowChange(release.id);
	}, [isRowLocked, onRowChange, release.id]);

	return (
		<Row key={rowIndex} index={rowIndex}>
			{columns.map((column, index) => {
				const key = column.id + index;

				switch (column.id) {
					case ArtistDiscographyTableColumn.TITLE:
						return (
							<ArtistDiscographyTitleCell
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								key={key}
								release={release}
								display={column.display}
							/>
						);
					case ArtistDiscographyTableColumn.RELEASE_DATE:
						return (
							<TextCell
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								key={key}
								textAlign="center"
								value={release.releaseDate}
								display={column.display}
							/>
						);
					case ArtistDiscographyTableColumn.CHECKBOX:
						return (
							<CheckboxCell
								key={key}
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								isChecked={isRowSelected}
								isDisabled={isRowLocked}
								onChange={handleChange}
								display={column.display}
							/>
						);
					case ArtistDiscographyTableColumn.STATUS:
						return (
							<StatusCell
								key={key}
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								value={release.status}
								display={column.display}
							/>
						);
					case ArtistDiscographyTableColumn.PROVIDER_NAME:
						return (
							<TextCell
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								key={key}
								textAlign="center"
								value={release.providerName || '-'}
								display={column.display}
							/>
						);
					case ArtistDiscographyTableColumn.LABEL_NAME:
						return (
							<TextCell
								data-testid={`artist-discography-cell-${column.id}-${rowIndex}`}
								key={key}
								textAlign="center"
								value={release.labelName || '-'}
								display={column.display}
							/>
						);
					default:
						return null;
				}
			})}
		</Row>
	);
};

export default ArtistDiscographyTableRow;
